/**
 * BrandingTab - Logo upload, video upload, and branding settings
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Image, Upload, X, Loader2, Film, Download } from 'lucide-react';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BrandingTab({ content, setContent }) {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [compressOnUpload, setCompressOnUpload] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const updateBranding = (field, value) => {
    setContent(prev => ({ ...prev, branding: { ...prev.branding, [field]: value } }));
  };

  const handleLogoUpload = async (file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error('File size must be less than 5MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      updateBranding('logoUrl', response.data.url);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
      setLogoFile(null);
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleVideoUpload = async (file) => {
    if (file.size > 50 * 1024 * 1024) { toast.error('Video file size must be less than 50MB'); return; }
    setVideoFile(file);
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload-video?compress=${compressOnUpload ? 'true' : 'false'}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      const newUrl = response.data.url;
      const newSize = response.data.size || file.size;
      const newPoster = response.data.poster_url || null;
      const existing = Array.isArray(content.branding?.backgroundVideos) ? [...content.branding.backgroundVideos] : [];
      // Migrate any legacy single-URL video into the library so it's never lost
      const legacyUrl = content.branding?.backgroundVideoUrl;
      if (legacyUrl && !existing.some(v => v.url === legacyUrl)) {
        existing.unshift({ id: `legacy_${Date.now()}`, url: legacyUrl, label: 'Previous video' });
      }
      const newItem = {
        id: `vid_${Date.now()}`,
        url: newUrl,
        label: file.name.replace(/\.[^.]+$/, '').slice(0, 40) || `Video ${existing.length + 1}`,
        size: newSize,
        posterUrl: newPoster,
      };
      const nextList = [...existing, newItem];
      setContent(prev => ({
        ...prev,
        branding: {
          ...prev.branding,
          backgroundVideos: nextList,
          // Keep the existing selection; only fall back to the new one if nothing is selected
          backgroundVideoUrl: prev.branding?.backgroundVideoUrl || newUrl,
        }
      }));
      if (newPoster) {
        const compressed = response.data.compressed;
        const origSize = response.data.original_size;
        if (compressed && origSize && newSize) {
          const pct = Math.round((1 - newSize / origSize) * 100);
          toast.success(`Video uploaded — compressed by ${pct}% (${formatSize(origSize)} → ${formatSize(newSize)}). Poster image created.`);
        } else if (compressOnUpload && !compressed) {
          toast.success('Video uploaded — already well-compressed, kept original. Poster image created.');
        } else {
          toast.success('Video uploaded — poster image created (shown while video loads)');
        }
      } else {
        toast.warning('Video uploaded, but the poster image could not be created');
      }
      setVideoFile(null);
    } catch (error) {
      toast.error('Failed to upload video');
      setVideoFile(null);
    } finally {
      setUploadingVideo(false);
    }
  };

  // Build the effective video library: saved list + any legacy single URL not in the list.
  const videoLibrary = (() => {
    const list = Array.isArray(content.branding?.backgroundVideos) ? [...content.branding.backgroundVideos] : [];
    const legacyUrl = content.branding?.backgroundVideoUrl;
    if (legacyUrl && !list.some(v => v.url === legacyUrl)) {
      list.unshift({ id: 'legacy', url: legacyUrl, label: 'Current video' });
    }
    return list;
  })();

  const selectVideo = (url) => {
    setContent(prev => {
      const list = Array.isArray(prev.branding?.backgroundVideos) ? [...prev.branding.backgroundVideos] : [];
      // Persist any legacy URL into the library the moment the admin interacts with it
      const legacyUrl = prev.branding?.backgroundVideoUrl;
      if (legacyUrl && !list.some(v => v.url === legacyUrl)) {
        list.unshift({ id: `legacy_${Date.now()}`, url: legacyUrl, label: 'Previous video' });
      }
      if (!list.some(v => v.url === url)) {
        list.push({ id: `vid_${Date.now()}`, url, label: 'Video' });
      }
      return {
        ...prev,
        branding: { ...prev.branding, backgroundVideos: list, backgroundVideoUrl: url },
      };
    });
  };

  const renameVideo = (id, newLabel) => {
    const list = (content.branding?.backgroundVideos || []).map(v => v.id === id ? { ...v, label: newLabel } : v);
    updateBranding('backgroundVideos', list);
  };

  const selectVideoForMobile = (url) => {
    setContent(prev => ({
      ...prev,
      branding: { ...prev.branding, backgroundVideoUrlMobile: url },
    }));
  };

  const clearMobileVideo = () => {
    setContent(prev => ({
      ...prev,
      branding: { ...prev.branding, backgroundVideoUrlMobile: '' },
    }));
  };

  const removeVideo = (id) => {
    const list = (content.branding?.backgroundVideos || []);
    const victim = list.find(v => v.id === id);
    if (!victim) return;
    const label = victim.label || 'this video';
    if (!window.confirm(`Are you sure you want to delete "${label}"? This cannot be undone.`)) {
      return;
    }
    const nextList = list.filter(v => v.id !== id);
    setContent(prev => {
      const branding = { ...prev.branding, backgroundVideos: nextList };
      if (branding.backgroundVideoUrl === victim.url) {
        branding.backgroundVideoUrl = nextList[0]?.url || '';
      }
      if (branding.backgroundVideoUrlMobile === victim.url) {
        branding.backgroundVideoUrlMobile = '';
      }
      return { ...prev, branding };
    });
  };

  // Auto-generate posters for videos that don't have one yet (legacy videos
  // uploaded before poster support). This lets the hero render an instant
  // first-frame image instead of a black box while the video buffers.
  const [generatingPosters, setGeneratingPosters] = useState({});
  const [posterErrors, setPosterErrors] = useState({});

  const generatePoster = async (v) => {
    const match = (v.url || '').match(/\/api\/videos\/([^/]+)/);
    if (!match) return;
    const fileId = match[1];
    setGeneratingPosters((prev) => ({ ...prev, [v.id]: true }));
    setPosterErrors((prev) => { const { [v.id]: _drop, ...rest } = prev; return rest; });
    try {
      const resp = await axios.post(`${API}/videos/${fileId}/generate-poster`, {}, { headers });
      const posterUrl = resp?.data?.poster_url;
      if (posterUrl) {
        setContent((prev) => {
          const l = Array.isArray(prev.branding?.backgroundVideos) ? [...prev.branding.backgroundVideos] : [];
          const next = l.map((x) => (x.id === v.id ? { ...x, posterUrl } : x));
          return { ...prev, branding: { ...prev.branding, backgroundVideos: next } };
        });
        toast.success('Poster image generated');
      } else {
        setPosterErrors((prev) => ({ ...prev, [v.id]: true }));
      }
    } catch (e) {
      setPosterErrors((prev) => ({ ...prev, [v.id]: true }));
    } finally {
      setGeneratingPosters((prev) => { const { [v.id]: _drop, ...rest } = prev; return rest; });
    }
  };

  useEffect(() => {
    const list = Array.isArray(content.branding?.backgroundVideos) ? content.branding.backgroundVideos : [];
    list.forEach((v) => {
      if (v.posterUrl || generatingPosters[v.id] || posterErrors[v.id]) return;
      generatePoster(v);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.branding?.backgroundVideos]);

  // Fetch byte size for videos missing it (legacy entries or cross-session imports)
  const [probedSizes, setProbedSizes] = useState({});
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    videoLibrary.forEach((v) => {
      if (v.size || probedSizes[v.url]) return;
      const href = v.url.startsWith('/api') ? `${backend}${v.url}` : v.url;
      fetch(href, { method: 'HEAD' })
        .then((r) => {
          const len = r.headers.get('content-length');
          if (len) setProbedSizes((prev) => ({ ...prev, [v.url]: parseInt(len, 10) }));
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.branding?.backgroundVideos, content.branding?.backgroundVideoUrl]);

  const formatSize = (bytes) => {
    if (!bytes || bytes <= 0) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  const logoSrc = logoPreview || content.branding?.logoUrl;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Branding</CardTitle>
        <CardDescription>Manage your app logo and brand identity</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo Upload */}
        <div>
          <Label className="text-base font-medium">App Logo</Label>
          <p className="text-sm text-gray-500 mb-4">Upload your logo. It will appear in the navigation, footer, and authentication pages.</p>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {logoSrc ? (
                <div className="relative">
                  <img src={logoSrc} alt="Logo Preview" className="w-24 h-24 object-contain rounded-xl border-2 border-gray-200 bg-white p-2" onError={(e) => e.target.style.display = 'none'} />
                  <Button type="button" variant="destructive" size="icon" className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                    onClick={() => { setLogoFile(null); setLogoPreview(null); updateBranding('logoUrl', ''); }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                  <Image className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <input type="file" id="logo-upload" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden"
                onChange={(e) => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file); }} />
              <Button type="button" variant="outline" onClick={() => document.getElementById('logo-upload').click()} disabled={uploadingLogo} className="rounded-xl">
                {uploadingLogo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />{logoSrc ? 'Change Logo' : 'Upload Logo'}</>)}
              </Button>
              <p className="text-xs text-gray-500">Max 5MB. JPEG, PNG, GIF, or WebP</p>
              {logoFile && <p className="text-xs text-emerald-600">{logoFile.name}</p>}
            </div>
          </div>
        </div>

        {/* Logo Preview on Different Backgrounds */}
        {logoSrc && (
          <div className="pt-4 border-t">
            <Label className="text-sm font-medium mb-3 block">Preview on Different Backgrounds</Label>
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gray-900 rounded-lg">
                <img src={logoSrc} alt="Logo on dark" className="w-12 h-12 object-contain" />
                <p className="text-xs text-gray-400 mt-1 text-center">Dark</p>
              </div>
              <div className="p-4 bg-white border rounded-lg">
                <img src={logoSrc} alt="Logo on light" className="w-12 h-12 object-contain" />
                <p className="text-xs text-gray-500 mt-1 text-center">Light</p>
              </div>
              <div className="p-4 bg-[#007a33] rounded-lg">
                <img src={logoSrc} alt="Logo on brand" className="w-12 h-12 object-contain" />
                <p className="text-xs text-white/70 mt-1 text-center">Brand</p>
              </div>
            </div>
          </div>
        )}

        {/* Splash Screen Background Colour */}
        <div className="pt-4 border-t">
          <Label className="text-base font-medium">Splash Screen Background</Label>
          <p className="text-sm text-gray-500 mb-4">Choose the background colour for the welcome splash screen displayed when you log in.</p>
          <div className="flex items-start gap-6">
            <div
              className="w-24 h-24 rounded-xl border-2 border-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ backgroundColor: content.branding?.splashColor || '#064E3B' }}
              data-testid="splash-color-preview"
            >
              <span className="text-white text-xs font-medium drop-shadow">Preview</span>
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={content.branding?.splashColor || '#064E3B'}
                  onChange={(e) => updateBranding('splashColor', e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border-0"
                  data-testid="splash-color-input"
                />
                <Input
                  value={content.branding?.splashColor || '#064E3B'}
                  onChange={(e) => updateBranding('splashColor', e.target.value)}
                  placeholder="#064E3B"
                  className="w-32 font-mono text-sm"
                  data-testid="splash-color-hex"
                />
              </div>
              <div className="grid grid-cols-8 gap-1.5">
                {['#064E3B','#1e3a5f','#7c3aed','#dc2626','#0369a1','#0f172a','#78350f','#4338ca','#be185d','#115e59','#374151','#854d0e','#166534','#1e40af','#7e22ce','#9f1239'].map(c => (
                  <button
                    key={c}
                    onClick={() => updateBranding('splashColor', c)}
                    className={`w-full aspect-square rounded-md transition-all border-2 ${content.branding?.splashColor === c ? 'border-gray-900 scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* App Name and Tagline */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
          <div>
            <Label>App Name</Label>
            <Input value={content.branding?.appName || ''} onChange={(e) => updateBranding('appName', e.target.value)} placeholder="Grassroots Coaching" className="mt-1" />
          </div>
          <div>
            <Label>Tagline</Label>
            <Input value={content.branding?.tagline || ''} onChange={(e) => updateBranding('tagline', e.target.value)} placeholder="Team Management Made Simple" className="mt-1" />
          </div>
        </div>

        {/* Theme Picker — admin chooses which themes appear in the public switcher.
            Min 1 must stay enabled (the switcher hides itself when only one is on). */}
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3" data-testid="landing-theme-picker">
          <div>
            <Label className="text-base font-medium">Landing Themes</Label>
            <p className="text-xs text-gray-500 mt-1">Choose which themes appear in the public theme switcher. Disabled themes are still available via direct link but hidden from the toggle. At least one must be enabled.</p>
          </div>
          {[
            { key: 'dark', name: 'Dark Navy', desc: 'Original dark gray-900 background', swatch: '#111827' },
            { key: 'black', name: 'Pure Black', desc: 'Monochrome blacks (#111111 / #1a1a1a / #262626)', swatch: '#111111' },
            { key: 'light', name: 'Brand (Light)', desc: 'White background with brand-green nav', swatch: '#ffffff' },
          ].map((opt) => {
            const enabled = Array.isArray(content.branding?.enabledThemes)
              ? content.branding.enabledThemes.includes(opt.key)
              : (opt.key === 'dark' || opt.key === 'light');
            const list = Array.isArray(content.branding?.enabledThemes)
              ? content.branding.enabledThemes
              : ['dark', 'light'];
            const wouldEmpty = enabled && list.length === 1;
            return (
              <div key={opt.key} className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md border border-gray-300" style={{ backgroundColor: opt.swatch }} />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{opt.name}</div>
                    <div className="text-xs text-gray-500">{opt.desc}</div>
                  </div>
                </div>
                <Switch
                  checked={enabled}
                  disabled={wouldEmpty}
                  onCheckedChange={(checked) => {
                    const current = Array.isArray(content.branding?.enabledThemes)
                      ? [...content.branding.enabledThemes]
                      : ['dark', 'light'];
                    let next;
                    if (checked) {
                      next = current.includes(opt.key) ? current : [...current, opt.key];
                    } else {
                      next = current.filter(k => k !== opt.key);
                      if (next.length === 0) { toast.error('At least one theme must stay enabled'); return; }
                    }
                    // Preserve canonical order (dark, black, light) for predictable cycling
                    const order = ['dark', 'black', 'light'];
                    next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                    updateBranding('enabledThemes', next);
                  }}
                  data-testid={`theme-toggle-${opt.key}`}
                />
              </div>
            );
          })}

          {/* Default theme — applied on first visit (or when no saved
              preference exists). Limited to the enabled themes above so the
              admin can't pick a hidden default. */}
          {(() => {
            const list = Array.isArray(content.branding?.enabledThemes) && content.branding.enabledThemes.length > 0
              ? content.branding.enabledThemes
              : ['dark', 'light'];
            const meta = {
              dark:  { name: 'Dark Navy',     swatch: '#111827' },
              black: { name: 'Pure Black',    swatch: '#111111' },
              light: { name: 'Brand (Light)', swatch: '#ffffff' },
            };
            const current = list.includes(content.branding?.defaultTheme)
              ? content.branding.defaultTheme
              : list[0];
            return (
              <div className="border-t border-gray-200 pt-3" data-testid="landing-default-theme-picker">
                <Label className="text-sm font-medium">Default theme</Label>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">First-time visitors see this theme. Returning visitors keep their last-used choice.</p>
                <div className="flex flex-wrap gap-2">
                  {list.map((key) => {
                    const m = meta[key] || { name: key, swatch: '#888' };
                    const isOn = current === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => updateBranding('defaultTheme', key)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                          isOn ? 'bg-[#007a33] text-white border-[#007a33] shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`default-theme-${key}`}
                      >
                        <span className="w-3.5 h-3.5 rounded-full border border-gray-400" style={{ backgroundColor: m.swatch }} />
                        {m.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Show / hide the public theme switcher button. When OFF, visitors
              can't change theme — they always see the default. */}
          <div className="border-t border-gray-200 pt-3 flex items-center justify-between gap-3" data-testid="landing-theme-switcher-toggle">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Show theme switcher</Label>
              <p className="text-xs text-gray-500 mt-0.5">Hide the floating sun/moon button so visitors can't change theme.</p>
            </div>
            <Switch
              checked={content.branding?.showThemeSwitcher !== false}
              onCheckedChange={(checked) => updateBranding('showThemeSwitcher', checked)}
              data-testid="theme-switcher-visible"
            />
          </div>

          {/* Nav background while over the hero. Three modes:
              • Solid (default) — keeps the theme background everywhere.
              • Frosted — translucent + backdrop blur over the hero, snaps to
                solid theme bg once the hero scrolls under the nav.
              • Transparent — no background, no border over the hero; snaps to
                solid theme bg once scrolled past.
              `navFrostedOverHero=true` is preserved for backwards
              compatibility and is treated as "frosted". */}
          <div className="border-t border-gray-200 pt-3" data-testid="landing-nav-hero-style">
            <Label className="text-sm font-medium">Nav background over hero</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">Style of the top bar while the visitor is still over the hero. Always reverts to the solid theme background once they scroll past.</p>
            {(() => {
              const current = content.branding?.navHeroStyle
                || (content.branding?.navFrostedOverHero === true ? 'frosted' : 'solid');
              const options = [
                { value: 'solid',       label: 'Solid' },
                { value: 'frosted',     label: 'Frosted glass' },
                { value: 'transparent', label: 'Transparent' },
              ];
              return (
                <div className="flex flex-wrap gap-2">
                  {options.map(o => {
                    const isOn = current === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => updateBranding('navHeroStyle', o.value)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                          isOn ? 'bg-[#007a33] text-white border-[#007a33] shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`nav-hero-style-${o.value}`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Mobile Header Display */}
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3" data-testid="landing-mobile-header-options">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Show app name on mobile</Label>
              <p className="text-xs text-gray-500 mt-0.5">Hide to keep only the logo in the landing-page header on phones.</p>
            </div>
            <Switch
              checked={content.branding?.app_name_mobile_show !== false}
              onCheckedChange={(checked) => updateBranding('app_name_mobile_show', checked)}
              data-testid="landing-nav-mobile-show"
            />
          </div>
          {content.branding?.app_name_mobile_show !== false && (
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Font size on mobile</Label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: 'sm', label: 'Small' },
                  { v: 'base', label: 'Medium' },
                  { v: 'lg', label: 'Large' },
                  { v: 'xl', label: 'Extra Large' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => updateBranding('app_name_mobile_size', opt.v)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      (content.branding?.app_name_mobile_size || 'base') === opt.v
                        ? 'bg-[#007a33] text-white border-[#007a33]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
                    }`}
                    data-testid={`landing-nav-mobile-size-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400">Long names automatically wrap onto two lines on small screens.</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-200">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Auto-hide nav on scroll down</Label>
              <p className="text-xs text-gray-500 mt-0.5">Hides the top nav when the user scrolls down and reveals it again when they scroll up.</p>
            </div>
            <Switch
              checked={content.branding?.nav_auto_hide === true}
              onCheckedChange={(checked) => updateBranding('nav_auto_hide', checked)}
              data-testid="landing-nav-auto-hide"
            />
          </div>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800"><strong>Tip:</strong> For best results, use a square logo with a transparent background. Recommended size is at least 200x200 pixels.</p>
        </div>

        {/* Background Video Library */}
        <div className="pt-6 border-t">
          <Label className="text-base font-medium">Background Videos</Label>
          <p className="text-sm text-gray-500 mb-2">Upload any number of videos. Use the <strong>Desktop</strong> button to set the video for desktop &amp; tablet, and <strong>Mobile</strong> for phones. If you don't pick a Mobile video, the Desktop video plays everywhere.</p>
          {content.branding?.backgroundVideoUrlMobile && (
            <button
              type="button"
              onClick={clearMobileVideo}
              className="text-xs text-gray-500 underline hover:text-gray-700 mb-2"
              data-testid="clear-mobile-video"
            >
              Clear mobile video (fall back to desktop on phones)
            </button>
          )}

          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <input type="file" id="video-upload" accept="video/mp4,video/webm,video/ogg" className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; if (file) handleVideoUpload(file); e.target.value = ''; }} />
            <Button type="button" variant="outline" onClick={() => document.getElementById('video-upload').click()} disabled={uploadingVideo} className="rounded-xl" data-testid="video-add-btn">
              {uploadingVideo ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{compressOnUpload ? 'Compressing & uploading…' : 'Uploading...'}</>) : (<><Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Video</>)}
            </Button>
            <p className="text-xs text-gray-500">Max 50MB. MP4, WebM or OGG.</p>
          </div>
          <label className="flex items-start gap-2 mb-4 select-none cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              checked={compressOnUpload}
              onChange={(e) => setCompressOnUpload(e.target.checked)}
              disabled={uploadingVideo}
              className="mt-0.5 h-4 w-4 accent-[#007a33] cursor-pointer"
              data-testid="video-compress-toggle"
            />
            <span>
              Compress on upload <span className="text-gray-500">(re-encodes H.264 CRF 28, strips audio — typically reduces file size 30–60%. Disable if your videos are already optimised.)</span>
            </span>
          </label>

          {videoLibrary.length === 0 ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-dashed border-gray-300 text-gray-500">
              <Film className="w-6 h-6 text-gray-400" strokeWidth={1.5} />
              <span className="text-sm">No background videos uploaded yet.</span>
            </div>
          ) : (
            <div className="space-y-2" data-testid="video-library">
              {videoLibrary.map((v) => {
                const active = content.branding?.backgroundVideoUrl === v.url;
                const activeMobile = content.branding?.backgroundVideoUrlMobile === v.url;
                const editable = v.id !== 'legacy';
                return (
                  <div
                    key={v.id}
                    className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${active || activeMobile ? 'border-emerald-500 bg-emerald-50/60' : 'border-gray-200 bg-white'}`}
                    data-testid={`video-row-${v.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => selectVideo(v.url)}
                      onMouseEnter={(e) => { const vid = e.currentTarget.querySelector('video'); if (vid) vid.play().catch(() => {}); }}
                      onMouseLeave={(e) => { const vid = e.currentTarget.querySelector('video'); if (vid) { vid.pause(); vid.currentTime = 0; } }}
                      className="shrink-0 rounded-md overflow-hidden border border-gray-200 hover:ring-2 hover:ring-emerald-400 transition-all bg-gray-100"
                      title="Hover to preview, click to use on desktop"
                      data-testid={`video-select-${v.id}`}
                    >
                      <video
                        src={v.url}
                        poster={v.posterUrl ? (v.posterUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${v.posterUrl}` : v.posterUrl) : undefined}
                        className="w-28 h-16 object-cover"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />
                    </button>
                    <div className="flex-1 min-w-0">
                      {editable ? (
                        <Input
                          value={v.label || ''}
                          onChange={(e) => renameVideo(v.id, e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Video label"
                          data-testid={`video-label-${v.id}`}
                        />
                      ) : (
                        <span className="text-sm text-gray-500 italic">Current video (uploaded before library)</span>
                      )}
                      <p className="text-[11px] mt-1 flex items-center gap-2 flex-wrap text-gray-400">
                        {(active || activeMobile) && (
                          <span className="text-emerald-600 font-medium">
                            Active on{active && activeMobile ? ' Desktop & Mobile' : active ? ' Desktop' : ' Mobile'}
                          </span>
                        )}
                        {(v.size || probedSizes[v.url]) && (
                          <span className="text-gray-400 font-normal" data-testid={`video-size-${v.id}`}>
                            · {formatSize(v.size || probedSizes[v.url])}
                          </span>
                        )}
                        {v.posterUrl ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-normal" data-testid={`video-poster-ready-${v.id}`}>
                            · Poster ready (shown while video loads)
                          </span>
                        ) : generatingPosters[v.id] ? (
                          <span className="text-gray-500 font-normal" data-testid={`video-poster-generating-${v.id}`}>
                            · Creating poster…
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => generatePoster(v)}
                            className="text-amber-600 underline font-normal hover:text-amber-700"
                            data-testid={`video-poster-generate-${v.id}`}
                          >
                            · Generate poster
                          </button>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        variant={active ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => selectVideo(v.url)}
                        className={active ? 'bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-xs' : 'h-8 px-3 text-xs'}
                        title="Use on desktop and tablet"
                        data-testid={`video-use-${v.id}`}
                      >
                        {active ? 'Desktop ✓' : 'Desktop'}
                      </Button>
                      <Button
                        type="button"
                        variant={activeMobile ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => selectVideoForMobile(v.url)}
                        className={activeMobile ? 'bg-emerald-600 hover:bg-emerald-700 h-8 px-3 text-xs' : 'h-8 px-3 text-xs'}
                        title="Use on mobile devices"
                        data-testid={`video-use-mobile-${v.id}`}
                      >
                        {activeMobile ? 'Mobile ✓' : 'Mobile'}
                      </Button>
                      {v.posterUrl && (
                        <a
                          href={v.posterUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${v.posterUrl}` : v.posterUrl}
                          download={`${(v.label || 'poster').replace(/[^a-z0-9_\-]+/gi, '_')}.jpg`}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                          title="Download poster image"
                          data-testid={`video-poster-download-${v.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      {editable && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVideo(v.id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`video-remove-${v.id}`}
                          title="Remove"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800"><strong>Note:</strong> The video gradually fades out as users scroll down the page.</p>
          </div>

          {/* Video Speed + Opacity Controls */}
          {content.branding?.backgroundVideoUrl && (
            <div className="mt-6 pt-6 border-t space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Playback Speed</Label>
                    <p className="text-xs text-gray-500">How fast the background video plays.</p>
                  </div>
                  <span className="text-sm font-medium text-gray-700" data-testid="video-speed-value">
                    {(content.branding?.videoPlaybackSpeed ?? 0.5).toFixed(2)}x
                  </span>
                </div>
                <input
                  type="range" min="0.25" max="2" step="0.05"
                  value={content.branding?.videoPlaybackSpeed ?? 0.5}
                  onChange={(e) => updateBranding('videoPlaybackSpeed', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#007a33]"
                  data-testid="video-speed-slider"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>0.25x (slowest)</span>
                  <span>1x (normal)</span>
                  <span>2x (fastest)</span>
                </div>
              </div>

              {/* Video Blur — soft-focus the background for premium look or
                  better text contrast over busy footage. */}
              <div className="pt-4 border-t space-y-3" data-testid="video-blur-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Background Video Blur</Label>
                    <p className="text-xs text-gray-500">Softens the background video so foreground text stays crisp. Great for busy footage.</p>
                  </div>
                  <Switch
                    checked={content.branding?.videoBlur === true}
                    onCheckedChange={(v) => updateBranding('videoBlur', v)}
                    data-testid="video-blur-toggle"
                  />
                </div>
                {content.branding?.videoBlur === true && (
                  <div data-testid="video-blur-amount-section">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm font-medium">Blur amount</Label>
                      <span className="text-sm font-medium text-gray-700" data-testid="video-blur-value">
                        {content.branding?.videoBlurAmount ?? 8}px
                      </span>
                    </div>
                    <input
                      type="range" min="1" max="30" step="1"
                      value={content.branding?.videoBlurAmount ?? 8}
                      onChange={(e) => updateBranding('videoBlurAmount', parseInt(e.target.value, 10))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#007a33]"
                      data-testid="video-blur-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>1px (subtle)</span>
                      <span>15px (heavy)</span>
                      <span>30px (frosted)</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t space-y-6">
              <Label className="text-base font-medium">Video Opacity Settings</Label>
              <p className="text-sm text-gray-500 -mt-4">Control how visible the background video appears on each theme.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dark Mode Opacity */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Dark Mode Opacity</Label>
                    <span className="text-sm font-medium text-gray-700">{content.branding?.videoOpacityDark ?? 30}%</span>
                  </div>
                  <input type="range" min="5" max="100" step="5" value={content.branding?.videoOpacityDark ?? 30}
                    onChange={(e) => updateBranding('videoOpacityDark', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-800" />
                  <div className="flex justify-between text-xs text-gray-400"><span>Subtle</span><span>Bold</span></div>
                  <div className="relative h-16 rounded-lg overflow-hidden bg-gray-900">
                    <video src={content.branding.backgroundVideoUrl} className="absolute inset-0 w-full h-full object-cover"
                      style={{ opacity: (content.branding?.videoOpacityDark ?? 30) / 100 }} muted loop autoPlay playsInline />
                    <div className="absolute inset-0 bg-gray-900/30" />
                    <div className="absolute inset-0 flex items-center justify-center"><span className="text-white text-xs font-medium">Dark Preview</span></div>
                  </div>
                </div>
                {/* Light Mode Opacity */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Light Mode Opacity</Label>
                    <span className="text-sm font-medium text-gray-700">{content.branding?.videoOpacityLight ?? 20}%</span>
                  </div>
                  <input type="range" min="5" max="100" step="5" value={content.branding?.videoOpacityLight ?? 20}
                    onChange={(e) => updateBranding('videoOpacityLight', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#007a33]" />
                  <div className="flex justify-between text-xs text-gray-400"><span>Subtle</span><span>Bold</span></div>
                  <div className="relative h-16 rounded-lg overflow-hidden bg-white border">
                    <video src={content.branding.backgroundVideoUrl} className="absolute inset-0 w-full h-full object-cover"
                      style={{ opacity: (content.branding?.videoOpacityLight ?? 20) / 100 }} muted loop autoPlay playsInline />
                    <div className="absolute inset-0 bg-[#111111]/30" />
                  <div className="absolute inset-0 flex items-center justify-center"><span className="text-white text-xs font-medium">Light Preview</span></div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          )}
        </div>

      </CardContent>
    </Card>
  );
}
